import router from '@/router';
import { useAlertStore } from '@/stores/alert';
import { useAdminStore } from '@/stores/admin';
import { useSiteStore } from '@/stores/site';

const buildHttpQuery = (data, url) => {
    const queryString = Object.keys(data).map((key) => {
        const val = data[key];
        const value =
            typeof val == 'object'
                ? JSON.stringify(val)
                : encodeURIComponent(val);
        return key + '=' + value;
    });
    return url + '?' + queryString.join('&');
};

const buildHttpBody = (data) => {
    let params = JSON.parse(JSON.stringify(data));
    Object.keys(params).forEach((key) => {
        if (typeof params[key] == 'object')
            params[key] =
                params[key] === null ? null : JSON.stringify(params[key]);
    });
    return params;
};

/**
 * Makes an HTTP request to the API using the provided method
 * @author Cameron Arnold
 * @param {string} path            API model path (eg. "auth/register", "user", etc)
 * @param {object} data            Content of the request
 * @param {string} method          Method type (eg. "POST", "GET", etc)
 * @param {boolean} isURLEncoded   True? format data in url query.  False? in body
 * @return {object}                Response from API
 */
export const httpAPI = async (
    url,
    data,
    method,
    router = null,
    stream = false,
    onChunk = null
) => {
    return new Promise(function (resolve, reject) {
        // Build headers
        let headers = {
            'Content-type': 'application/json',
            Accept: 'application/json',
        };

        if (stream) headers['x-no-compression'] = true;

        // Build payload
        let payload = {
            method: method,
            credentials: 'include',
        };

        if (!data) data = {};

        const overrideCompanyId = useAdminStore().companyId;
        if (overrideCompanyId !== null && overrideCompanyId !== undefined) {
            data.company_id = overrideCompanyId;
        }

        const overrideSiteId = useSiteStore().globalSiteId;
        if (overrideSiteId !== null && overrideSiteId !== undefined) {
            data.site_id = overrideSiteId;
        }

        const isURLEncoded = method == 'GET';
        if (isURLEncoded) {
            url = buildHttpQuery(data, url);
        } else {
            const params = buildHttpBody(data);
            payload.body = JSON.stringify(params);
        }

        // Finally set headers
        payload.headers = new Headers(headers);

        // Send fetch request, and return result
        fetch(url, payload)
            .then(async function (response) {
                if (response.status == 401) {
                    return resolve({
                        success: false,
                        message: 'Unauthorized',
                        status: 401,
                    });
                } else if (response.ok) {
                    // Handle stream
                    const contentType = response.headers.get('Content-Type');

                    if (contentType == 'text/event-stream') {
                        const reader = response.body.getReader();
                        const decoder = new TextDecoder('utf-8');
                        let isDone = false;

                        do {
                            const { done, value } = await reader.read();
                            isDone = done;

                            const chunkText = decoder.decode(value, {
                                stream: true,
                            });
                            const eventRegex =
                                /event: (.+?)\ndata: (.*?)(?=\n\n$|\n\nevent:|$)/gs; // beware ye who enters
                            const matches = chunkText.matchAll(eventRegex);

                            for (const match of matches) {
                                onChunk({ event: match[1], data: match[2] });
                            }
                        } while (!isDone);

                        resolve(null);
                    } else {
                        const jsonText = await response.json();
                        resolve(jsonText);
                    }
                }
            })
            .catch((e) => {
                const alertStore = useAlertStore();
                alertStore.pushError('Failed to connect to API');
                reject(e);
            });
    });
};

export const getFileBlob = (url, data = null) => {
    //https://stackoverflow.com/questions/22724070/prompt-file-download-with-xmlhttprequest/44435573#44435573

    return new Promise((resolve, reject) => {
        const overrideCompanyId = useAdminStore().companyId;
        if (overrideCompanyId !== null && overrideCompanyId !== undefined) {
            data.company_id = overrideCompanyId;
        }

        if (data) url = buildHttpQuery(data, url);

        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.withCredentials = true;

        xhr.onload = function (e) {
            const blob = e.currentTarget.response;

            // JSON? That means there was probably an error :( Parse it.
            if (blob.type == 'application/json') {
                const reader = new FileReader();
                reader.onload = function () {
                    try {
                        const json = JSON.parse(reader.result);
                        reject(json);
                    } catch (error) {
                        reject({
                            success: false,
                            message: 'Failed to read error from blob',
                        });
                    }
                };

                reader.onerror = function () {
                    reject({
                        success: false,
                        message: 'Failed to read error from blob',
                    });
                };

                reader.readAsText(blob);
            } else {
                const contentDispo = e.currentTarget.getResponseHeader(
                    'Content-Disposition'
                );
                const fileName = contentDispo
                    .match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)[1]
                    .replaceAll('"', '');
                resolve([blob, fileName]);
            }
        };

        xhr.send();
    });
};

export const downloadFile = (url, data = null) => {
    return new Promise((resolve, reject) => {
        getFileBlob(url, data)
            .then(([blob, fileName]) => {
                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = fileName;
                a.dispatchEvent(new MouseEvent('click'));
                resolve([blob, fileName]);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

/**
 * Downloads a client-side generated blob (e.g., CSV, JSON) as a file
 * @param {Blob} blob - The blob to download
 * @param {string} fileName - The filename for the download
 * @returns {Promise<void>}
 */
export const downloadBlob = async (blob, fileName) => {
    let url;
    try {
        // Create the blob URL
        url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.visibility = 'hidden';
        // Append to DOM and trigger the download
        document.body.appendChild(a);
        a.dispatchEvent(new MouseEvent('click'));
        document.body.removeChild(a);
        // Wait for a short delay before revoking the URL
        await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
        // Catch any errors that occur during the process
        console.error('Download failed:', error);
        throw error;
    } finally {
        // Always clean up the blob URL
        if (url) {
            URL.revokeObjectURL(url);
        }
    }
};
